/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from "react-dom";
import { FiX, FiTrash2, FiMinus, FiPlus, FiPackage, FiShoppingBag, FiUser, FiPhone, FiMapPin, FiHome, FiChevronLeft } from 'react-icons/fi';
import { buildImageUrl } from '../theme1/utils/theme1Helpers';
import { formatNumber } from '../../utils/formatNumber';

export default function EditOrderModal({ order, products = [], theme, onCancel, onSave }) {
    const [draft, setDraft] = useState(null);
    const initialized = useRef(false);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        if (!order || initialized.current) return;
        initialized.current = true;

        const clean = JSON.parse(JSON.stringify(order));
        clean.details = (clean.details || []).map(d => ({
            ...d,
            itemId: d.itemId || d.ItemId,
            variantId: d.variantId || d.VariantId,
            itemName: d.itemName || d.ItemName,
            variantName: d.variantName || d.VariantName,
            colorName: d.colorName || d.ColorName,
            size: d.size || d.Size,
            quantity: Number(d.quantity || d.Quantity || 0),
            price: Number(d.price || d.Price || 0),
            discount: 0,
            imageUrl: d.imageUrl || d.ImageUrl,
            lineTotal: Number(d.lineTotal || d.LineTotal || (Number(d.quantity || d.Quantity || 0) * Number(d.price || d.Price || 0)) || 0)
        }));

        setDraft(clean);
    }, [order]);

    useEffect(() => { initialized.current = false; }, [order?.orderId]);

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [qty, setQty] = useState(1);
    const getOptionStock = (opt) => Number(opt?.stock ?? opt?.stockQty ?? 0);
    const resolveItemStock = (item) => {
        let currentStock = Number(item?.stockQty ?? 0);
        const prod = products.find(p => Number(p.id) === Number(item?.itemId));
        if (!prod) return Math.max(0, currentStock);
        const vari = prod.variants?.find(v => Number(v.variantId) === Number(item?.variantId));
        if (!vari) return Math.max(0, currentStock);
        const opt = (vari.options || vari.sizes || []).find(o => (o.label || o.size) === item?.size);
        if (opt) currentStock = getOptionStock(opt);
        return Math.max(0, currentStock);
    };

    const handleAddSelection = () => {
        if (!selectedVariant || !selectedSize) return;
        setDraft(prev => {
            const details = [...(prev.details || [])];
            const index = details.findIndex(d =>
                Number(d.variantId) === Number(selectedVariant.variantId) && d.size === selectedSize.label
            );
            const maxAvailable = getOptionStock(selectedSize);
            if (maxAvailable <= 0) {
                alert("هذا الخيار نفذ من المخزون");
                return prev;
            }

            if (index !== -1) {
                const item = details[index];
                const newQty = Number(item.quantity) + Number(qty);
                if (newQty > maxAvailable) {
                    alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
                    return prev;
                }
                details[index] = { ...item, quantity: newQty, lineTotal: newQty * Number(item.price) };
            } else {
                if (Number(qty) > maxAvailable) {
                    alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
                    return prev;
                }
                const finalPrice = Number(selectedVariant.finalPrice || selectedVariant.basePrice || 0);
                const basePrice = Number(selectedVariant.basePrice || 0);
                details.push({
                    id: 0,
                    orderId: prev.orderId,
                    itemId: Number(selectedProduct.id),
                    variantId: Number(selectedVariant.variantId),
                    itemName: selectedProduct.name,
                    variantName: selectedVariant.variantName || "",
                    colorName: selectedVariant.color?.hex || selectedVariant.color?.hexCode || selectedVariant.colorHex || "",
                    size: selectedSize.label || "",
                    quantity: Number(qty),
                    price: finalPrice,
                    imageUrl: selectedVariant.imageUrl || selectedProduct.mainImage,
                    stockQty: maxAvailable,
                    discount: basePrice - finalPrice,
                    lineTotal: finalPrice * Number(qty)
                });
            }
            return { ...prev, details };
        });
        setSelectedProduct(null);
        setSelectedVariant(null);
        setSelectedSize(null);
        setQty(1);
    };

    const updateQty = (i, delta) => {
        if (locked) return;
        setDraft(prev => {
            const details = [...prev.details];
            const item = details[i];
            if (!item) return prev;
            const q = Number(item.quantity) + delta;
            const currentStock = resolveItemStock(item);
            if (delta > 0 && q > currentStock) {
                alert(`وصلت للحد الأقصى المتاح في المخزون لهذه القطعة (${currentStock} قطعة)`);
                return prev;
            }
            if (q <= 0) details.splice(i, 1);
            else details[i] = { ...item, quantity: q, stockQty: currentStock, lineTotal: q * Number(item.price) };
            return { ...prev, details };
        });
    };

    const prepareOrder = () => {
        const totalAmount = draft.details.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
        return {
            ...draft,
            totalAmount,
            netAmount: totalAmount - Number(draft.discountAmount || 0),
            tenantID: Number(draft.tenantId),
            items: draft.details.map(d => ({
                itemId: Number(d.itemId),
                variantId: Number(d.variantId),
                itemName: d.itemName,
                variantName: d.variantName,
                colorName: d.colorName,
                size: d.size,
                quantity: Number(d.quantity),
                price: Number(d.price),
                imageUrl: d.imageUrl,
                stockQty: Number(d.stockQty || 0),
                discount: Number(d.discount || 0),
                lineTotal: Number(d.quantity) * Number(d.price)
            }))
        };
    };

    if (!draft) return null;
    const locked = Number(draft.isPaid) === 1;
    const orderTotal = draft.details.reduce((s, i) => s + Number(i.lineTotal), 0);

    return (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col font-sans overflow-hidden" dir="rtl">

            {selectedProduct &&
                createPortal(
                    <div className="fixed inset-0 z-[999999] bg-white flex flex-col overflow-hidden animate-fadeIn">
                        {/* Header */}
                        <div className="border-b border-gray-100 p-4 sm:p-6 flex items-center justify-between sticky top-0 bg-white z-20">
                            <div className="w-10"></div>
                            <h3 className="text-xl font-black text-gray-900 text-center">تخصيص المنتج</h3>
                            <button
                                onClick={() => { setSelectedProduct(null); setSelectedVariant(null); setSelectedSize(null); }}
                                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FiX className="text-2xl text-gray-500" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
                            <div className="max-w-2xl mx-auto space-y-8 pb-20">
                                {/* Product Preview */}
                                <div className="flex gap-4 sm:gap-6 bg-gray-50 p-6 sm:p-8 rounded-[2rem] items-center border border-gray-100">
                                    <img
                                        src={buildImageUrl(selectedVariant?.imageUrl || selectedProduct.mainImage)}
                                        className="w-20 h-20 sm:w-28 sm:h-28 object-contain rounded-2xl bg-white shadow-sm p-3"
                                        alt=""
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-black text-gray-900 text-xl sm:text-2xl truncate mb-1">{selectedProduct.name}</div>
                                        <div className="text-sm sm:text-base text-gray-400 font-bold truncate mb-2">{selectedVariant?.variantName || "اختر اللون أولاً"}</div>
                                        <div className={`text-2xl sm:text-3xl font-black ${theme.text}`}>
                                            {formatNumber(selectedVariant?.finalPrice || selectedVariant?.basePrice || selectedProduct.price)} <span className="text-sm">IQD</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Color Selection */}
                                <div className="space-y-4">
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest text-right">1. اختر اللون المفضل</label>
                                    <div className="flex flex-wrap gap-4 justify-end">
                                        {(() => {
                                            const colorGroups = {};
                                            (selectedProduct.variants || []).forEach(v => {
                                                const hex = v.color?.hex || v.color?.hexCode || v.colorHex || "#333";
                                                if (!colorGroups[hex]) {
                                                    colorGroups[hex] = v;
                                                }
                                            });

                                            return Object.values(colorGroups).map(v => (
                                                <button
                                                    key={v.variantId}
                                                    onClick={() => { setSelectedVariant(v); setSelectedSize(null); }}
                                                    className={`group relative p-1.5 rounded-full border-2 transition-all duration-300 ${selectedVariant?.variantId === v.variantId ? 'scale-115 shadow-xl rotate-3' : 'border-transparent hover:scale-105'}`}
                                                    style={selectedVariant?.variantId === v.variantId ? { borderColor: theme.primary } : {}}
                                                >
                                                    <div
                                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-black/5 shadow-inner"
                                                        style={{ background: v.color?.hex || v.color?.hexCode || v.colorHex }}
                                                    />
                                                    <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 whitespace-nowrap z-30 pointer-events-none transition-all font-bold shadow-xl">
                                                        {v.variantName}
                                                    </div>
                                                </button>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* Size Selection */}
                                {selectedVariant && (
                                    <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500">
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest text-right">2. اختر المقاس المناسب</label>
                                        <div className="flex flex-wrap gap-3 justify-end">
                                            {(selectedVariant.options || []).map(opt => {
                                                const isOutOfStock = getOptionStock(opt) <= 0;
                                                const isActive = selectedSize?.label === opt.label;
                                                return (
                                                    <button
                                                        key={opt.label}
                                                        disabled={isOutOfStock}
                                                        onClick={() => setSelectedSize(opt)}
                                                        className={`min-w-[70px] px-6 py-3.5 rounded-2xl border-2 text-sm font-black transition-all duration-300
                                                        ${isActive ? 'text-white shadow-2xl scale-105' : isOutOfStock ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed opacity-50' : 'bg-white border-gray-100 hover:border-gray-300 hover:bg-gray-50'}`}
                                                        style={isActive ? { backgroundColor: theme.primary, borderColor: theme.primary } : {}}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Quantity Selection */}
                                {selectedSize && (
                                    <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500">
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest text-right">3. حدد الكمية</label>
                                        <div className="flex items-center justify-between bg-gray-50 rounded-[2rem] p-4 sm:p-6 border border-gray-100">
                                            <div className="text-right">
                                                <div className="text-gray-900 font-black text-lg">الكمية المطلوبة</div>
                                                <div className="text-xs text-gray-400 font-bold">المتوفر حالياً: {getOptionStock(selectedSize)} قطعة</div>
                                            </div>
                                            <div className="flex items-center gap-4 bg-white rounded-2xl p-2 shadow-sm border border-gray-100">
                                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl hover:bg-gray-50 transition-all text-gray-400">
                                                    <FiMinus />
                                                </button>
                                                <span className="w-12 text-center font-black text-2xl text-gray-900">{qty}</span>
                                                <button
                                                    onClick={() => { if (qty < getOptionStock(selectedSize)) setQty(qty + 1); }}
                                                    disabled={qty >= getOptionStock(selectedSize)}
                                                    className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl hover:bg-gray-50 transition-all disabled:opacity-20 text-gray-900"
                                                >
                                                    <FiPlus />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer Action */}
                        <div className="border-t border-gray-100 p-6 sm:p-8 bg-white sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto">
                                <button
                                    disabled={!selectedSize || getOptionStock(selectedSize) <= 0}
                                    onClick={handleAddSelection}
                                    className={`w-full ${theme.bg} text-white py-5 sm:py-6 rounded-[2rem] font-black text-lg sm:text-xl shadow-2xl hover:shadow-blue-500/30 transition-all active:scale-95 flex items-center justify-center gap-4`}
                                >
                                    <FiPlus size={24} /> <span>تحديث تفاصيل الطلب</span>
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }

            {/* Main Content */}
            <div className="h-16 border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 bg-white sticky top-0 z-10">
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className={`${theme.bg} p-2 rounded-lg text-white`}>
                        <FiPackage className="text-xl" />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800">تعديل الطلب</h2>
                    <span className="bg-gray-100 px-2 py-0.5 rounded-lg text-xs font-bold text-gray-500 truncate">
                        #{draft.orderNo}
                    </span>
                </div>
                <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-800">
                    <FiX className="text-2xl" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50/30">
                <div className="max-w-7xl mx-auto w-full p-2 sm:p-4 lg:p-8">

                    {/* Order Info */}
                    <div className="bg-white rounded-2xl p-3 sm:p-5 shadow-sm border border-gray-100 mb-4 sm:mb-6">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
                            <div className="flex items-center gap-1 sm:gap-2">
                                <FiUser className="text-gray-400" />
                                <div>
                                    <span className="text-gray-400 block">العميل</span>
                                    <span className="font-bold text-gray-800">{draft.customerName}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                                <FiPhone className="text-gray-400" />
                                <div>
                                    <span className="text-gray-400 block">الهاتف</span>
                                    <span className="font-bold text-gray-800">{draft.customerPhone1}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                                <FiMapPin className="text-gray-400" />
                                <div>
                                    <span className="text-gray-400 block">المحافظة</span>
                                    <span className="font-bold text-gray-800">{draft.city}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2">
                                <FiHome className="text-gray-400" />
                                <div>
                                    <span className="text-gray-400 block">العنوان</span>
                                    <span className="font-bold text-gray-800 line-clamp-1">{draft.address}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                        {/* Products Sidebar */}
                        <div className="lg:col-span-5 xl:col-span-4">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden lg:sticky lg:top-24 max-h-[60vh]">
                                <div className="p-4 sm:p-5 border-b border-gray-100 flex justify-between items-center">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <span className={`w-1 h-5 ${theme.bg} rounded-full`}></span> المعرض المتاح
                                    </h3>
                                    <span className="text-[10px] sm:text-[11px] bg-gray-100 px-2 py-1 rounded-lg font-bold text-gray-500">{products.length} منتج</span>
                                </div>

                                <div className="overflow-y-auto p-2 sm:p-4 space-y-2 sm:space-y-3 max-h-[50vh] sm:max-h-[60vh]">
                                    {(!products || products.length === 0) ? (
                                        <div className="py-16 text-center text-gray-400 text-sm">جاري جلب المنتجات...</div>
                                    ) : products.map(p => {
                                        // 1. Group variants by color
                                        const colorGroups = {};
                                        (p.variants || []).forEach(v => {
                                            const hex = v.color?.hex || v.color?.hexCode || "#333";
                                            if (!colorGroups[hex]) {
                                                colorGroups[hex] = v;
                                            }
                                        });
                                        const uniqueVariantsCount = Object.keys(colorGroups).length;

                                        // 2. Calculate final price (sync with theme1)
                                        const firstVariant = p.variants?.[0];
                                        let finalDisplayPrice = 0;
                                        if (firstVariant) {
                                            const basePrice = Number(firstVariant.price || 0);
                                            const preCalculatedDiscount = Number(firstVariant.discount || 0);
                                            const preCalculatedFinal = Number(firstVariant.finalPrice || (basePrice - preCalculatedDiscount));
                                            const dValue = Number(p.discountValue || 0);
                                            const dType = Number(p.discountType || 0);
                                            finalDisplayPrice = preCalculatedFinal;
                                            if (preCalculatedDiscount === 0 && dValue > 0) {
                                                let discountAmount = dType === 1 ? (basePrice * dValue) / 100 : dValue;
                                                finalDisplayPrice = basePrice - discountAmount;
                                            }
                                        } else {
                                            finalDisplayPrice = Number(p.price || 0);
                                        }

                                        return (
                                            <div
                                                key={p.id}
                                                onClick={() => { setSelectedProduct(p); if (p.variants?.length > 0) setSelectedVariant(p.variants[0]); }}
                                                className="flex gap-2 sm:gap-3 p-2 sm:p-3 bg-gray-50 hover:bg-gray-100 cursor-pointer rounded-xl items-center transition-colors"
                                            >
                                                <img src={buildImageUrl(p.mainImage)} alt="" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg bg-white p-1" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-gray-800 truncate">{p.name}</div>
                                                    <div className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">{uniqueVariantsCount} ألوان متاحة</div>
                                                    <div className={`font-black text-sm sm:text-base ${theme.text} mt-1`}>
                                                        {formatNumber(finalDisplayPrice)} IQD
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Order Details */}
                        <div className="lg:col-span-7 xl:col-span-8 flex flex-col gap-4">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                {draft.details.length === 0 ? (
                                    <div className="p-10 text-center text-gray-400">لا توجد عناصر مضافة بعد</div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {draft.details.map((d, i) => (
                                            (() => {
                                                const itemStock = resolveItemStock(d);
                                                const reachedMax = Number(d.quantity) >= itemStock;
                                                return (
                                                    <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3">
                                                        <img src={buildImageUrl(d.imageUrl)} alt="" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="truncate font-bold text-gray-800">{d.itemName}</div>
                                                            <div className="text-[10px] sm:text-xs text-gray-400 truncate">{d.variantName} - {d.size}</div>
                                                            <div className="font-black text-sm sm:text-base mt-1">{formatNumber(d.price)} د.ع</div>
                                                        </div>
                                                        <div className="flex items-center gap-1 sm:gap-2">
                                                            <button onClick={() => updateQty(i, -1)} className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-500"><FiMinus /></button>
                                                            <span className="w-6 sm:w-8 text-center font-bold">{d.quantity}</span>
                                                            <button
                                                                disabled={locked || reachedMax || itemStock <= 0}
                                                                onClick={() => updateQty(i, 1)}
                                                                className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                                                            >
                                                                <FiPlus />
                                                            </button>
                                                            <button onClick={() => updateQty(i, -d.quantity)} className="p-1 text-red-500 hover:text-red-700 transition-colors"><FiTrash2 /></button>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ))}
                                    </div>
                                )}

                                {/* Order Total */}
                                <div className="border-t border-gray-100 p-4 sm:p-5 flex justify-between items-center">
                                    <div className="font-bold text-gray-800">المجموع</div>
                                    <div className="font-black text-lg sm:text-xl text-gray-900">{formatNumber(orderTotal)} د.ع</div>
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={() => onSave(prepareOrder())}
                                disabled={locked}
                                className={`w-full py-3 sm:py-4 font-bold text-white rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2 ${locked ? 'bg-gray-400 cursor-not-allowed' : theme.bg}`}
                            >
                                <FiShoppingBag /> حفظ التعديلات
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
